import React, { useState, useCallback, FC, useEffect, useRef } from 'react';
import { debounce } from 'lodash';
import { Markdown } from '@src/components/Markdown';
import { Container } from '@src/components/Container';
import { CircularLoading, Size } from '@src/components/CircularLoading';
import { Sidebar } from '@src/components/Sidebar';
import { setErrorFlashMessage } from '@src/redux/actions/flashMessages';
import { useGenerateOpeningPitchMutation, useSaveOpeningMutation } from '@src/graphql/generated';
import { ActiveTab } from '@src/components/Sidebar/enums/activeTab.enum';
import { trackEvent, trackViewCreateApplication } from '@src/metrics';
import { MetricAction, MetricContext, MetricEventSource } from '@src/metrics/metrics.enum';
import { parseEmojis } from '@src/util/parseEmojis';
import { Appearance, Button } from '@src/components/Button';
import { FUNNEL_APPLICATIONS_ROUTE, FUNNEL_REVIEW_ROUTE } from '@src/Routes/links';
import { Avatar } from '@src/components/Avatar';
import { useAppDispatch } from '@src/redux';
import { PITCH_ROUTE } from '@src/components/Preview/appRoutes';
import { ContainerType } from '@src/components/Container/enums/containerType.enum';
import { AiSuggestionButton } from '@src/components/AiSuggestionButton';
import { PITCH_MIN_LENGTH } from '../constants/textLength';
import { CreatePitchProps } from './props';
import './styles.scss';

const MESSAGE_INSTRUCTIONS = ['Write an invitation to chat with the candidate about your open position.'];

export const CreatePitch: FC<CreatePitchProps> = ({ recruiter, company, opening, isLoading }) => {
  const dispatch = useAppDispatch();
  const abortControllerRef = useRef(new AbortController());
  const [pitch, setPitch] = useState(opening.pitch || '');

  const shouldEnableAiButtons = !!company?.hasPublishedProfile;

  const [saveOpening] = useSaveOpeningMutation({
    onError: () => dispatch(setErrorFlashMessage()),
  });

  const onUpdatePitch = useCallback(
    debounce(async (pitch: string) => {
      saveOpening({
        variables: {
          openingId: opening.id,
          openingInput: {
            pitch: pitch,
          },
        },
      });
    }, 300),
    [],
  );

  const handleChange = (pitch: string) => {
    abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    setPitch(pitch);
    onUpdatePitch(pitch);
  };

  const [generateOpeningPitch, { loading: isAiSuggestionLoading }] = useGenerateOpeningPitchMutation({
    context: {
      fetchOptions: {
        signal: abortControllerRef.current.signal,
      },
    },
    onCompleted: ({ generateOpeningPitch }) => {
      if (generateOpeningPitch) {
        handleChange(generateOpeningPitch);
      } else {
        dispatch(setErrorFlashMessage('Generating the pitch is failing at the moment. Please try again later.'));
      }
    },
    onError: () => {
      dispatch(setErrorFlashMessage('Generating the pitch is failing at the moment. Please try again later.'));
    },
    variables: {
      openingId: opening.id,
    },
  });

  useEffect(() => {
    if (shouldEnableAiButtons && !opening.pitch && !isAiSuggestionLoading) {
      generateOpeningPitch();
    }
  }, []);

  const textLength = pitch.trim().length;

  return (
    <div className="page funnels create-pitch">
      <div className="page-layout">
        <Sidebar
          benefits={[]}
          companyProfile={company?.profile}
          defaultTab={ActiveTab.PREVIEW}
          opening={opening}
          previewUrl={PITCH_ROUTE}
          recruiter={recruiter}
        />

        <Container className="create-pitch__content" type={ContainerType.BORDERED}>
          {isLoading ? (
            <div className="create-pitch__loading-container">
              <CircularLoading className="create-pitch__loading" size={Size.LARGE} />
            </div>
          ) : (
            <div className="create-pitch">
              <div className="create-pitch__header">
                Pitch message
                {shouldEnableAiButtons && (
                  <AiSuggestionButton
                    isLoading={isAiSuggestionLoading}
                    onRegenerateClick={() => generateOpeningPitch()}
                  />
                )}
              </div>

              <div className="create-pitch__text-content">
                <Avatar
                  avatarColor={recruiter?.avatarColor}
                  initials={recruiter?.nameInitials}
                  size="x-large"
                  source={recruiter?.avatarUrl}
                />

                <div className="create-pitch__input">
                  <div className="create-pitch__input-name">{recruiter?.fullName}</div>

                  <textarea
                    className="create-pitch__message"
                    onBlur={() =>
                      trackEvent({
                        context: MetricContext.CREATE_OPENING_PITCH,
                        interaction: MetricAction.SET_PITCH_MESSAGE,
                        payload: { 'Opening Id': opening.id },
                      })
                    }
                    onChange={event => handleChange(parseEmojis(event.target.value))}
                    placeholder="Personal message that will catch attention..."
                    value={pitch}
                  />
                </div>
              </div>

              <div className="create-pitch__count">
                {`Minimum ${PITCH_MIN_LENGTH} characters. ${textLength}/${PITCH_MIN_LENGTH}`}
              </div>

              <div className="create-pitch__info">
                <div className="create-pitch__info-title">
                  Pitch is a custom message which is sent to candidates you accept via Sourcing
                </div>

                {MESSAGE_INSTRUCTIONS.map(instruction => (
                  <div className="create-pitch__info-item" key={instruction}>
                    <span className="create-pitch__info-bullet" />

                    <Markdown markdown={instruction} wrapperClassName="create-pitch__info-text" />
                  </div>
                ))}
              </div>

              <div className="create-pitch__footer">
                <Button
                  isDisabled={!opening.id || textLength < PITCH_MIN_LENGTH}
                  isLink
                  onClick={() =>
                    trackViewCreateApplication({
                      'Event Source': MetricEventSource.CONTINUE_BUTTON,
                      'Opening Id': opening.id,
                      'Previous Context': MetricContext.CREATE_OPENING_PITCH,
                    })
                  }
                  params={{ openingId: opening.id }}
                  to={FUNNEL_REVIEW_ROUTE}
                >
                  Continue
                </Button>

                <Button
                  appearance={Appearance.INVISIBLE}
                  className="create-pitch__button-back"
                  isLink
                  onClick={() =>
                    trackViewCreateApplication({
                      'Event Source': MetricEventSource.BACK_BUTTON,
                      'Opening Id': opening.id,
                      'Previous Context': MetricContext.CREATE_OPENING_PITCH,
                    })
                  }
                  params={{ openingId: opening.id }}
                  to={FUNNEL_APPLICATIONS_ROUTE}
                >
                  Back
                </Button>
              </div>
            </div>
          )}
        </Container>
      </div>
    </div>
  );
};
